import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getRealEstateStyle } from '../../data/realEstate';
import { cn } from '../cn';
import {
  EGYPT_BOX_CONFIG,
  type RandomBoxConfig,
} from './randomBoxConfig';

const STAR_ICON = '/ui/telegram-stars-icon.png';
const RANDOM_BOX_GIFT_IMAGE = '/ui/random-box-gift.webp';

interface RandomBoxProps {
  plotId: number | null;
  onBuy: (realEstateId: number) => void;
  isProcessing: boolean;
  getRealEstateName: (id: number) => string;
  config: RandomBoxConfig;
  endTime?: Date | string | null;
}

export default function RandomBox({ plotId, onBuy, isProcessing, getRealEstateName, config, endTime }: RandomBoxProps) {
  const { t } = useTranslation();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [expandedHouseId, setExpandedHouseId] = useState<number | null>(null);

  // Вічний 3-денний таймер: обчислюємо кінець поточного 3-денного циклу
  const getNextCycleEnd = (): Date => {
    const now = new Date();
    const startOfYear = new Date(Date.UTC(now.getUTCFullYear(), 0, 0));
    const oneDay = 1000 * 60 * 60 * 24;
    const dayOfYear = Math.floor((now.getTime() - startOfYear.getTime()) / oneDay);
    const remainder = dayOfYear % 3;
    const daysUntilNext = remainder === 0 ? 3 : 3 - remainder;
    return new Date(Date.UTC(now.getUTCFullYear(), 0, dayOfYear + daysUntilNext));
  };

  const calculateTimeLeft = (): string => {
    const target = endTime
      ? (typeof endTime === 'string' ? new Date(endTime) : endTime)
      : getNextCycleEnd();

    const now = new Date();
    const diff = Math.max(0, target.getTime() - now.getTime());

    if (diff <= 0) return '0d 0h 0m 0s';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  };

  // (a)/(b) Initial + endTime change: derive display string; keep state only for 1s ticks
  const [timeLeft, setTimeLeft] = useState<string>(() => calculateTimeLeft());
  const [prevEndTime, setPrevEndTime] = useState(endTime);
  if (endTime !== prevEndTime) {
    setPrevEndTime(endTime);
    setTimeLeft(calculateTimeLeft());
  }

  // (c) Second-resolution timer — setState only in interval callback
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // calculateTimeLeft closes over endTime; re-bind interval when target changes
    // eslint-disable-next-line react-hooks/exhaustive-deps -- endTime is the sole external input
  }, [endTime]);

  const handleBuy = () => {
    if (!plotId || isProcessing) return;
    const randomIndex = Math.floor(Math.random() * config.houseIds.length);
    const selectedHouseId = config.houseIds[randomIndex];
    onBuy(selectedHouseId);
  };

  const houseIcons = config.houseIds.map(id => ({
    id,
    image: `/game-assets/map-objects/real-estate/${id}.webp`,
    name: getRealEstateName(id),
  }));

  return (
    <article
      className="relative flex flex-col gap-lg rounded-xl border-2 border-solid border-daily-quest-auxiliary-modals-border-2 bg-[linear-gradient(130.37deg,var(--color-black)_1.67%,var(--color-random-box-surface)_51.68%,var(--color-black)_97.03%)] px-2xl py-[13px]"
    >
      {/* NEW badge */}
      {config.isNew && (
        <div
          className="absolute top-[-10px] right-xl z-[2] rounded-pill border border-solid border-danger-strong bg-random-box-surface-2 px-[10px] py-[2px]"
        >
          <span
            className="whitespace-nowrap [font-family:Lato,sans-serif] text-[11px] font-bold text-white"
          >
            {/* "NEW" here means "Novelty / New arrival" — a product badge, not an adjective. Translate as noun: "Новинка" (uk), "Neuheit" (de), "Novedad" (es) */}
            {t("NEW")}
          </span>
        </div>
      )}

      {/* Таймер по центру зверху */}
      <div className="flex w-full flex-wrap items-center justify-center gap-sm">
        <div
          className="flex items-center justify-center rounded-pill bg-black-alpha-60 px-lg py-[3px]"
        >
          <span
            className="whitespace-nowrap [font-family:Lato,sans-serif] text-[12px] leading-[16px] font-normal text-white"
          >
            {t("Time left")}: {timeLeft}
          </span>
        </div>
      </div>

      {/* Іконки будинків */}
      <div
        className="flex w-full items-center justify-center gap-[4.225px]"
      >
        {houseIcons.map((house) => (
          <div
            key={house.id}
            onClick={() => setExpandedHouseId(house.id)}
            className="relative box-border flex h-[58.0875px] w-[67.21px] cursor-pointer items-center justify-center rounded-[9.6025px] border-[1.6875px] border-solid border-reward-highlight bg-[url('/ui/tap-screen/house-background.webp')] bg-cover bg-center bg-no-repeat p-[4.8px]"
          >
            <img
              src={house.image}
              alt={house.name}
              className="h-full w-full object-contain"
              onError={(e) => {
                const style = getRealEstateStyle(house.id);
                (e.target as HTMLImageElement).src = style.image;
              }}
            />
            {/* Zoom hint icon */}
            <svg
              width="12" height="12" viewBox="0 0 16 16" fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="pointer-events-none absolute top-1 right-1 opacity-50"
            >
              <circle cx="7" cy="7" r="4" stroke="white" strokeWidth="1.5" fill="none" />
              <path d="M10 10L13 13" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
        ))}
      </div>

      {/* Центральна частина: зображення боксу + кнопка покупки */}
      <div className="flex items-center justify-center gap-lg">
        {/* Зображення подарункового боксу */}
        <div className="relative inline-block">
          <div
            className="pointer-events-none absolute inset-[-21.67%]"
          >
            <div
              className="h-full w-full rounded-full bg-[radial-gradient(circle,var(--color-reward-highlight-alpha-30)_0%,transparent_70%)]"
            />
          </div>
          <img
            src={RANDOM_BOX_GIFT_IMAGE}
            alt="Random box"
            className="block aspect-[128/135] h-auto min-w-20 w-[min(200px,30vw)] max-w-[min(200px,30vw)] shrink rounded-[18px] object-contain object-[50%_50%]"
          />
        </div>

        {/* Кнопка покупки */}
        <div className="flex min-w-0 flex-col items-center gap-md">
          <p
            className="m-0 text-center [font-family:Lato,sans-serif] text-[11px] leading-[16px] font-bold tracking-[-0.747px] whitespace-pre-wrap text-white not-italic"
          >
            {t("Get now")}
          </p>
          <div className="flex w-full flex-col items-center gap-xs">
            <button
              onClick={handleBuy}
              disabled={isProcessing || !plotId}
              className={cn(
                'box-border mr-[2px] inline-flex w-fit items-center justify-center gap-[5px] whitespace-nowrap rounded-pill border-none bg-white px-[min(16px,4.5vw)] py-sm [font-family:"Rubik_One",sans-serif] text-[14px] leading-[29px] font-normal tracking-[-0.747px] text-text-dark',
                isProcessing || !plotId ? 'cursor-not-allowed opacity-60' : 'cursor-pointer opacity-100',
              )}
            >
              <span
                className="line-through [font-family:Lato,sans-serif] text-[12px] leading-[16px] font-normal text-daily-quest-auxiliary-modals-border-2 [text-decoration-skip-ink:none]"
              >
                {config.originalPrice}
              </span>
              <span
                className='[font-family:"Rubik_One",sans-serif] text-[14px] leading-[29px] font-normal text-random-box-surface-2'
              >
                {config.discountPrice}
              </span>
              <img
                src={STAR_ICON}
                alt="Star"
                className="h-5 w-[19px] shrink-0"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Лайтбокс: збільшене зображення будинку */}
      {expandedHouseId !== null && (() => {
        const house = houseIcons.find(h => h.id === expandedHouseId);
        if (!house) return null;
        return (
          <div
            onClick={() => setExpandedHouseId(null)}
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-xl bg-black-alpha-85"
          >
            <div
              className="box-border flex h-[min(80vw,320px)] w-[min(80vw,320px)] items-center justify-center rounded-[18px] border-2 border-solid border-reward-highlight bg-[url('/ui/tap-screen/house-background.webp')] bg-cover bg-center bg-no-repeat p-lg"
            >
              <img
                src={house.image}
                alt={house.name}
                className="h-full w-full object-contain"
                onError={(e) => {
                  const s = getRealEstateStyle(house.id);
                  (e.target as HTMLImageElement).src = s.image;
                }}
              />
            </div>
            <p
              className="m-0 text-center [font-family:Lato,sans-serif] text-[16px] font-bold text-white"
            >
              {house.name}
            </p>
            <p
              className="m-0 [font-family:Lato,sans-serif] text-[12px] text-white-alpha-50"
            >
              {t("Tap anywhere to close")}
            </p>
          </div>
        );
      })()}

      {/* Нижня частина: назва та опис */}
      <div className="w-full">
        <p
          className="m-0 self-stretch [font-family:Lato,sans-serif] text-[16px] leading-[29px] font-bold tracking-[-0.747px] text-white not-italic"
        >
          {config === EGYPT_BOX_CONFIG
            ? t("Desert Sands Mystery Box")
            : t("Bikini Bottom House Box")}
        </p>
        <p
          className="mt-sm self-stretch [font-family:-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',Arial,sans-serif] text-[14px] leading-[20px] font-normal tracking-[0px] text-white not-italic"
        >
          {config === EGYPT_BOX_CONFIG
            ? t("A rare mystery box filled with ancient desert architecture. Contains iconic landmarks of the pharaohs' world. For those who seek power, legacy, and timeless grandeur.")
            : t("A limited mystery box inspired by life under the sea. Contains exclusive iconic houses with bright ocean charm. Limited-time offer.")}
        </p>
      </div>
    </article>
  );
}
